import { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Card,
  CardContent,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import { detail_addon_form, detail_user_form } from "../../DAL/form/userForm";
import { s3baseUri } from "../../config/config";
import DashboardHeader from "../../components/headers/DashboardHeader";
import { visa_application } from "../../components/SideBarData/SidebarData";

const getFileTypeIcon = (fileUrl) => {
  const extension = fileUrl?.path?.split(".").pop().toLowerCase();

  if (["jpg", "jpeg", "png", "gif", "webp"].includes(extension)) {
    return "image";
  }
  if (extension === "pdf") {
    return <PictureAsPdfIcon style={{ color: "#15978a" }} />;
  }
  if (["doc", "docx"].includes(extension)) {
    return <DescriptionIcon style={{ color: "#15978a" }} />;
  }
  if (["xls", "xlsx"].includes(extension)) {
    return <InsertDriveFileIcon style={{ color: "#15978a" }} />;
  }

  return <InsertDriveFileIcon style={{ color: "#15978a" }} />;
};

const renderAnswer = (question) => {
  if (question.question_type === "view_file") {
    return (
      <div
        className="mt-2"
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <DescriptionIcon style={{ color: "#15978a" }} />
        <a
          href={s3baseUri + question.answer}
          target="_blank"
          rel="noopener noreferrer"
          style={{ color: "#1976d2", textDecoration: "underline" }}
        >
          View Document
        </a>
      </div>
    );
  }

  if (question.question_type === "file") {
    if (Array.isArray(question.answer) && question.answer.length > 0) {
      return (
        <div style={{ display: "flex", flexWrap: "wrap", gap: "1rem" }}>
          {question.answer.map((fileUrl, idx) => {
            const fileType = getFileTypeIcon(fileUrl);
            return (
              <div className="mt-2" key={idx} style={{ width: "200px" }}>
                {fileType === "image" ? (
                  <a
                    href={s3baseUri + fileUrl.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pointer"
                  >
                    <img
                      src={s3baseUri + fileUrl.path}
                      alt={`Uploaded file ${idx + 1}`}
                      style={{
                        maxWidth: "100%",
                        borderRadius: "4px",
                        display: "block",
                      }}
                    />
                  </a>
                ) : (
                  <div
                    className="mt-2"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: "8px",
                    }}
                  >
                    {fileType}
                    <a
                      href={s3baseUri + fileUrl.path}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "#1976d2", textDecoration: "underline" }}
                    >
                      View Document {idx + 1}
                    </a>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    } else {
      return "No file uploaded";
    }
  }

  if (question.question_type === "boxwithcheck") {
    return question.answer === true ? (
      <span className="">✔️ {question.question_statement}</span>
    ) : null;
  }

  if (
    question.question_type === "description" ||
    question.question_type === "statement"
  ) {
    return (
      <span style={{ fontWeight: "bold" }}>
        <div
          dangerouslySetInnerHTML={{ __html: question.question_statement }}
        ></div>
      </span>
    );
  }

  if (question.question_type === "electronic_sign") {
    return question.answer ? (
      <a
        href={s3baseUri + question.answer}
        target="_blank"
        rel="noopener noreferrer"
        className="pointer"
      >
        <img
          src={s3baseUri + question.answer}
          alt="Electronic Signature"
          style={{ borderRadius: 4, display: "block" }}
        />
      </a>
    ) : (
      "No signature provided"
    );
  }

  if (
    question.question_type === "checkbox" &&
    Array.isArray(question.answer) &&
    question.options
  ) {
    const selectedLabels = question.options
      .filter((opt) => question.answer.includes(opt.value))
      .map((opt) => opt.label);
    return selectedLabels.length > 0
      ? selectedLabels.map((label, i) => (
          <span className="d-flex gap-1">
            ✔️
            <div key={i} dangerouslySetInnerHTML={{ __html: label }} />
          </span>
        ))
      : "No options selected";
  }

  if (question.options && Array.isArray(question.options)) {
    const selected = question.options.find(
      (opt) => opt.value === question.answer
    );
    return selected ? (
      <span dangerouslySetInnerHTML={{ __html: selected.label }} />
    ) : (
      "N/A"
    );
  }

  return question?.answer?.startsWith("file") ? (
    <div
      className="mt-2"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
      }}
    >
      {getFileTypeIcon({ path: question?.answer })}
      <a
        href={s3baseUri + question?.answer}
        target="_blank"
        rel="noopener noreferrer"
        style={{
          textDecoration: "underline",
        }}
      >
        View Document
      </a>
    </div>
  ) : (
    <span>{question?.answer || "N/A"}</span>
  );
};

const PreviewAnswers = () => {
  const navigate = useNavigate();
  const [userList, setUserList] = useState({});
  const [initialLoading, setInitialLoading] = useState(true);

  const handleFetchDetail = async () => {
    setInitialLoading(true);
    const queryParams = new URLSearchParams(window.location.search);
    const addonId = queryParams.get("addon_id");

    let result;
    let localPrimaryHousingInfo = null;

    if (addonId) {
      result = await detail_addon_form(addonId);
      // Fetch primary housing info for addons
      const primaryUserResult = await detail_user_form();
      if (primaryUserResult.code === 200) {
        const hSec = primaryUserResult.form?.form?.find(
          (s) => s.value === "housing_option"
        );
        if (hSec) {
          localPrimaryHousingInfo = {};
          hSec.questions.forEach((q) => {
            localPrimaryHousingInfo[q.created_for] = q.answer;
          });
        }
      }
    } else {
      result = await detail_user_form();
    }

    if (result.code === 200) {
      const formData = result?.form?.form || [];
      const mergedForm = visa_application.map((templateSection) => {
        const existingSection = formData.find(
          (s) => s.value === templateSection.value
        );
        let section = existingSection
          ? JSON.parse(JSON.stringify(existingSection))
          : JSON.parse(JSON.stringify(templateSection));

        // Sync housing info for addons
        if (
          addonId &&
          section.value === "housing_option" &&
          localPrimaryHousingInfo
        ) {
          section.questions = section.questions.map((q) => {
            if (localPrimaryHousingInfo[q.created_for] !== undefined) {
              return { ...q, answer: localPrimaryHousingInfo[q.created_for] };
            }
            return q;
          });
          // Mark as completed for addon
          section.is_completed = true;
        }

        return section;
      });

      setUserList({
        ...result.form,
        form: mergedForm,
      });
      setInitialLoading(false);
    } else {
      setInitialLoading(false);
    }
  };

  useEffect(() => {
    handleFetchDetail();
  }, []);

  if (initialLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{
          height: "100vh",
        }}
      >
        <CircularProgress color="primary" />
      </div>
    );
  }

  const checkcountry =
    userList?.form
      ?.find((q) => q.value === "visa_type_and_application_details")
      ?.questions?.find((q) => q.created_for === "country_of_residence")
      ?.answer === "haiti";

  const updatedData = userList?.form
    ?.filter((s) => !s.hide_section)
    .map((section) => {
      if (section?.value === "required_documentation") {
        return {
          ...section,
          questions: section?.questions.filter((q) => {
            if (q.created_for === "dcpj_data_page") {
              return checkcountry;
            }
            return true;
          }),
        };
      }
      // Hide housing nested questions if housing_need is not 'yes'
      if (section?.value === "housing_option") {
        const housingNeed = section?.questions.find(
          (q) => q.created_for === "housing_need"
        )?.answer;

        if (housingNeed !== "yes") {
          return {
            ...section,
            questions: section.questions.filter(
              (q) => q.created_for === "housing_need"
            ),
          };
        }
      }

      return section;
    });

  return (
    <div className="header-container">
      <DashboardHeader />
      <div className="container">
        <div className="row d-flex mt-3">
          <div className="col-12 d-flex align-items-center mb-3">
            <IconButton onClick={() => navigate(-1)}>
              <ArrowBackIcon sx={{ fontSize: 20, color: "#15978a" }} />
            </IconButton>
            <h4
              className="ms-2 mb-0"
              style={{
                color: "#15978a",
              }}
            >
              {userList?.case_number
                ? `Case Number : ${userList?.case_number}`
                : userList?.addon_info
                ? `Applicant Name : ${userList?.addon_info?.first_name} ${userList?.addon_info?.last_name}`
                : ""}
            </h4>
          </div>
        </div>

        <Box id="form-to-export">
          {updatedData?.map((section, index) => {
            const filteredQuestions = section?.questions?.filter(
              (q) => q?.question_id !== "Z9ozEO"
            );
            return (
              <Box key={index} mb={4}>
                <Card
                  className="pdf-section"
                  sx={{
                    width: "100%",
                    boxShadow: 3,
                    borderLeft: "5px solid",
                    borderColor: "#15978a",
                  }}
                >
                  <CardContent>
                    <h2 className="mb-2">{section?.title}</h2>
                    {section?.value === "getting_started" && (
                      <h4 className="mb-2">
                        Eligibility Certification Statement
                      </h4>
                    )}
                    {section?.intro_text && (
                      <p className="mb-3">{section?.intro_text}</p>
                    )}
                    {filteredQuestions &&
                      filteredQuestions?.length > 0 &&
                      filteredQuestions?.map((q, idx) => (
                        <Box key={idx} mb={3}>
                          <p
                            className="mb-0"
                            dangerouslySetInnerHTML={{
                              __html: q.question_statement,
                            }}
                          />
                          {!q.is_not_show_ans && (
                            <p className="mb-0 mt-2 fw-600">
                              Answer: {renderAnswer(q)}
                            </p>
                          )}

                          {idx < filteredQuestions?.length - 1 &&
                            q.question_type !== "heading" && (
                              <Divider sx={{ my: 2 }} />
                            )}
                        </Box>
                      ))}

                    {section?.sub_sections?.map((sub, subIdx) => (
                      <Box key={subIdx} mt={subIdx > 0 ? 4 : 3}>
                        <h4>{sub.title}</h4>
                        {sub.questions.map((q, qIdx) => (
                          <Box key={qIdx} mb={2}>
                            <p className="mb-0">
                              {/<[a-z][\s\S]*>/i.test(q.question_statement) ? (
                                <span
                                  dangerouslySetInnerHTML={{
                                    __html: q.question_statement,
                                  }}
                                />
                              ) : (
                                q.question_statement
                              )}
                            </p>

                            <p className="mb-0 mt-2 fw-600">
                              Answer: {renderAnswer(q)}
                            </p>
                            {qIdx < sub.questions.length - 1 && (
                              <Divider sx={{ my: 1 }} />
                            )}
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </CardContent>
                </Card>
              </Box>
            );
          })}
        </Box>
      </div>
    </div>
  );
};

export default PreviewAnswers;
